import { Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle
} from '@angular/material/dialog';
import { MessagingService } from '../../../components/messaging/messaging.service';
import moment from 'moment';
import { EIO_DATE_UI_FORMAT_MOMENT } from '../../../consts/localization.const';
import { ToolbarService } from '../../../services/toolbar.service';
import { ConfirmBoxComponent } from '../../../components/confirm-box/confirm-box.component';
import { EscapeDialogHelper } from '../../../helpers/escape-dialog.helper';
import { DateInputHelper } from '../../../helpers/date-input.helper';
import { SysAdminService } from '../../../services/sys-admin.service';
import { ModalConfigsHelper } from '../../../helpers/modal-configs.helper';
import { smallModalConfig } from '../../../consts/modal-config.const';
import { BasicQuotingSystemView } from '../../../model/user.model';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatDivider } from '@angular/material/divider';
import { MatError, MatFormField, MatHint, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatOption, MatSelect } from '@angular/material/select';
import { MatInput } from '@angular/material/input';
import { MatDatepicker, MatDatepickerInput, MatDatepickerToggle } from '@angular/material/datepicker';
import { BrowserAutoFillDisabledDirective } from '../../../directives/browser-auto-fill-disabled.directive';

@Component({
  selector: 'app-change-quoting-system',
  templateUrl: './change-quoting-system.component.html',
  styleUrls: ['./change-quoting-system.component.scss'],
  imports: [
    MatDialogTitle,
    MatIconButton,
    MatDialogClose,
    MatIcon,
    MatDivider,
    MatDialogContent,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatSelect,
    MatOption,
    MatError,
    MatInput,
    MatDatepickerInput,
    BrowserAutoFillDisabledDirective,
    MatDatepickerToggle,
    MatSuffix,
    MatDatepicker,
    MatHint,
    MatDialogActions,
    MatButton
  ]
})
export class ChangeQuotingSystemComponent implements OnInit {
  moveQSForm: UntypedFormGroup;

  loading = signal(false);
  jobsCount = signal<number>(null);
  fromCompanyName = signal<string>('');
  toCompanyName = signal<string>('');
  initialDateString = signal<string>('');
  duplicateFromTo = signal(false);
  quotingSystemsList = signal<BasicQuotingSystemView[]>([]);

  movingQSMessage = computed(
    () =>
      `${this.jobsCount() ?? ''} job(s) will be moved from ${this.fromCompanyName()} to ${this.toCompanyName()} starting ${this.initialDateString()}.`
  );

  get initialDate() {
    return this.moveQSForm.get('initialDate');
  }
  get from() {
    return this.moveQSForm.get('from');
  }
  get to() {
    return this.moveQSForm.get('to');
  }

  data: { repairerSiteId: number } = inject(MAT_DIALOG_DATA);
  dialogRef = inject(MatDialogRef<ChangeQuotingSystemComponent>);
  dateInputHelper = inject(DateInputHelper);
  private confirmBox = inject(MatDialog);
  private fb = inject(UntypedFormBuilder);
  private message = inject(MessagingService);
  private toolbarService = inject(ToolbarService);
  private escapeDialogRef = inject(EscapeDialogHelper);
  private sysAdminService = inject(SysAdminService);
  private modalConfigsHelper = inject(ModalConfigsHelper);
  private destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    this.#getQuotingSystemList();

    this.escapeDialogRef.escapeDialog(this.dialogRef);

    this.moveQSForm = this.fb.group({
      from: ['', Validators.required],
      to: ['', Validators.required],
      initialDate: ['', Validators.required]
    });

    this.moveQSForm.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.getNumberOfJobs();
    });
  }

  #getQuotingSystemList(): void {
    this.sysAdminService.getRepairerSite(this.data.repairerSiteId).subscribe((repairerSite) => {
      if (repairerSite) {
        this.quotingSystemsList.set(repairerSite.quotingSystems);
      }
    });
  }

  getNumberOfJobs(): void {
    if (this.initialDate.valid && this.from.valid && this.to.valid) {
      if (this.from.value !== this.to.value) {
        this.duplicateFromTo.set(false);
        this.loading.set(true);
        this.fromCompanyName.set(this.quotingSystemsList().find((qs) => qs.clientId === this.from.value).companyName);
        this.toCompanyName.set(this.quotingSystemsList().find((qs) => qs.clientId === this.to.value).companyName);
        this.toolbarService.setCloudState('FETCHING');
        this.sysAdminService
          .getQuotingSystemJobCounts(
            this.from.value,
            this.data.repairerSiteId,
            moment(this.initialDate.value).startOf('day').utc().unix()
          )
          .subscribe({
            next: (jobsCount) => this.jobsCount.set(jobsCount),
            error: () => {
              this.message.error('Could not retrieve the jobs count for the quoting system.');
              this.toolbarService.setCloudState('RESTING');
              this.loading.set(false);
            },
            complete: () => {
              this.toolbarService.setCloudState('RESTING');
              this.loading.set(false);
              this.initialDateString.set(moment(this.initialDate.value).format(EIO_DATE_UI_FORMAT_MOMENT));
            }
          });
      } else {
        this.duplicateFromTo.set(true);
      }
    }
  }

  confirmMoveQuotingSystem(): void {
    const data = {
      title: 'Change Quoting System',
      alertMessage: this.movingQSMessage(),
      confirmButton: 'SAVE',
      hideCannotBeUndone: true
    };
    this.confirmBox
      .open(ConfirmBoxComponent, this.modalConfigsHelper.buildMediumModalConfig(data, smallModalConfig))
      .afterClosed()
      .subscribe((result) => {
        if (result === 'SAVE') {
          this.loading.set(true);
          this.toolbarService.setCloudState('SAVING');
          this.sysAdminService
            .updateQuotingSystemJob(
              this.from.value,
              this.data.repairerSiteId,
              this.to.value,
              moment(this.initialDate.value).startOf('day').utc().unix()
            )
            .subscribe({
              next: () => this.dialogRef.close('Success'),
              error: () => {
                this.message.error('Could not update the jobs of the quoting system.');
                this.toolbarService.setCloudState('RESTING');
                this.loading.set(false);
              },
              complete: () => {
                this.toolbarService.setCloudState('RESTING');
                this.loading.set(false);
              }
            });
        }
      });
  }
}
